import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Marker, Popup } from 'react-leaflet';
import Control from '@skyeer/react-leaflet-custom-control';
import { Switch } from 'antd';
import { DownOutlined, RightOutlined } from '@ant-design/icons';
import L from 'leaflet';
import { useIntl } from 'react-intl';
import { isSuccess } from '@iso/lib/helpers/apiResponse';
import { locationService } from '@iso/services';
import { TONE, TONE_COLOR, compareForRoster, relativeTime, resolveState } from './userLocationState';
import { useBrowserLocationReporter } from './useBrowserLocationReporter';

/**
 * A state is labelled either by a real area name, which is data and must not
 * be translated, or by a key when there is nothing to name.
 *
 * Returns a string rather than an element: everything rendered inside
 * <Control> lives in a separate React root with no intl context (see the `t`
 * helper below), so the translation has to be resolved before it gets there.
 */
const stateLabel = (state, t) => (state.label ? state.label : t(state.labelKey));

/**
 * Where the property's people are, from the shared user-location contract
 * (GET /api/web/locations) that the Main, Team and Contractor apps also use.
 *
 * Two halves, because a map alone cannot satisfy the display rules: pins for
 * the people with a position, and a roster panel listing EVERY member with
 * their status dot. The roster is where "(User Disabled)", "Off Property" and
 * "Location Unavailable" are shown — those people have no pin, and dropping
 * them off the screen entirely would hide the very states the contract asks
 * to be displayed.
 *
 * Any member of the property sees this. The privacy control is each person's
 * own sharing switch, not a role gate, and the switch for the signed-in user
 * sits at the bottom of the roster.
 */

/** Positions are reported in minutes, so a minute is as fresh as it gets. */
const REFRESH_MS = 60 * 1000;

/**
 * The roster covers the map, so it starts collapsed and remembers what the
 * viewer chose. Browser storage is per-viewer and can throw outright in a
 * private window, so every access is guarded and the panel renders fine
 * without it.
 */
const EXPANDED_KEY = 'airagri.peopleRoster.expanded';

const readExpanded = () => {
  try {
    return window.localStorage.getItem(EXPANDED_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const writeExpanded = (value) => {
  try {
    window.localStorage.setItem(EXPANDED_KEY, value ? '1' : '0');
  } catch (e) {
    // A viewer with site data blocked just loses the preference.
  }
};

/**
 * A row is either a property member or a guest -- a walk-up visitor, QR or
 * WhatsApp check-in, who has no account at all and so arrives with user_id
 * null. Keying on user_id alone would collide every guest onto one React key.
 */
/**
 * Why the switch is on but nothing is being shared. Every non-success state
 * needs an entry: a switch reading "on" beside an empty map, with no reason
 * given, is indistinguishable from the feature being broken -- which is how
 * this whole layer was found to be dead in the first place.
 */
const REPORT_PROBLEMS = {
  denied: 'propertyMapPage.userLocations.permissionDenied',
  unavailable: 'propertyMapPage.userLocations.positionUnavailable',
  refused: 'propertyMapPage.userLocations.reportRefused',
};

const personKey = (person) =>
  person.user_id ? `u${person.user_id}` : `v${person.visit_id}`;

/**
 * Guests are labelled by what they checked in as; members need no label.
 * Returns a translation key, not a string: the caller resolves it with `t`
 * before the text reaches the control (see the helper in the component).
 */
const personTypeKey = (person) => {
  if (person.person_type === 'contractor') {
    return 'propertyMapPage.userLocations.contractor';
  }
  if (person.person_type === 'visitor') {
    return 'propertyMapPage.userLocations.visitor';
  }
  return null;
};

const buildIcon = (tone) => {
  const fill = TONE_COLOR[tone] || TONE_COLOR[TONE.INACTIVE];
  const faded = tone !== TONE.LIVE;

  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36">
      <path d="M14 0C6.8 0 1 5.8 1 13c0 9.2 11.4 21.5 11.9 22a1.5 1.5 0 0 0 2.2 0C15.6 34.5 27 22.2 27 13 27 5.8 21.2 0 14 0z"
            fill="${fill}" stroke="#ffffff" stroke-width="2"/>
      <circle cx="14" cy="10" r="3.6" fill="#ffffff"/>
      <path d="M7.6 20.5c0-3.5 2.9-5.6 6.4-5.6s6.4 2.1 6.4 5.6z" fill="#ffffff"/>
    </svg>`;

  return L.divIcon({
    // Not `custom-icon-marker`: PropertyMap.styles.js forces that class to
    // 44x60, which would stretch this pin and move its anchor off the fix.
    className: 'user-location-marker',
    html: `<div style="opacity: ${faded ? 0.65 : 1};">${svg}</div>`,
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -32],
  });
};

const Dot = ({ tone }) => (
  <span
    style={{
      display: 'inline-block',
      width: 8,
      height: 8,
      borderRadius: '50%',
      background: TONE_COLOR[tone] || TONE_COLOR[TONE.INACTIVE],
      marginRight: 8,
      flex: '0 0 auto',
    }}
  />
);

const UserLocationMarkers = ({ propertyId }) => {
  const intl = useIntl();
  // Resolve translations HERE, in the component body, and put plain strings in
  // the JSX below.
  //
  // @skyeer/react-leaflet-custom-control renders its children with
  // ReactDOM.render into the Leaflet control element -- a separate React root
  // (its cleanup calls unmountComponentAtNode). React context does not cross
  // that boundary, so an <IntlMessages>/injectIntl consumer placed inside
  // <Control> throws "Could not find required `intl` object", and with no error
  // boundary around the layer that takes down the panel AND the pins with it.
  // useIntl() is safe because the component itself is in the normal tree.
  // DeviceMapMarker never hit this: its control holds a plain antd Button.
  const t = (id, values) => intl.formatMessage({ id }, values);
  const [people, setPeople] = useState([]);
  const [sharing, setSharing] = useState(null);
  const [savingSharing, setSavingSharing] = useState(false);
  const [expanded, setExpanded] = useState(readExpanded);
  const inFlight = useRef(false);
  // Toggling the layer off unmounts it mid-poll, which is routine here.
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);

  const load = useCallback(() => {
    if (!propertyId) {
      setPeople([]);
      return;
    }
    if (inFlight.current) {
      return;
    }

    inFlight.current = true;
    locationService
      .getList(propertyId)
      .then((res) => {
        if (!mounted.current) {
          return;
        }
        setPeople(isSuccess(res) ? res.data || [] : []);
      })
      .catch(() => {
        // A dropped poll is not worth a toast; the next tick retries.
      })
      .finally(() => {
        inFlight.current = false;
      });
  }, [propertyId]);

  useEffect(() => {
    load();

    const timer = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        return;
      }
      load();
    }, REFRESH_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        load();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // The signed-in user's own preference is read once per property — it only
  // changes when they change it here.
  useEffect(() => {
    if (!propertyId) {
      return;
    }
    locationService
      .getSharing(propertyId)
      .then((res) => {
        if (mounted.current && isSuccess(res)) {
          setSharing(!!res.data.sharing_enabled);
        }
      })
      .catch(() => {});
  }, [propertyId]);

  // The browser reports only while this layer is open and the user has sharing
  // switched on. Closing the map stops it -- nothing reports in the background.
  const { status: reportStatus, detail: reportDetail } = useBrowserLocationReporter(
    propertyId,
    sharing === true,
    REFRESH_MS,
    load
  );

  const toggleExpanded = () => {
    setExpanded((prev) => {
      writeExpanded(!prev);
      return !prev;
    });
  };

  // Whether the signed-in user's own row came from a mobile report AND still
  // has a pin to explain. The position test is not redundant: redactFor()
  // erases coordinates but leaves source = 'app', so after check-out -- or
  // once a fix ages past the cutoff -- the row still reads as app-sourced.
  // Without it the panel tells somebody their phone is placing a pin that is
  // not on the map, the opposite of the confusion it exists to settle.
  const ownRowIsFromApp = people.some(
    (p) => p.is_me && p.source === 'app' && resolveState(p).plottable
  );

  // People actually drawn on the map, which is not the same as the roster
  // length -- most of a property's membership has never reported.
  const locatedCount = people.filter((person) => resolveState(person).plottable).length;

  const onToggleSharing = (checked) => {
    setSavingSharing(true);
    locationService
      .setSharing(propertyId, checked)
      .then((res) => {
        if (!mounted.current) {
          return;
        }
        if (isSuccess(res)) {
          setSharing(!!res.data.sharing_enabled);
          // Your own row changes immediately — switching off drops the
          // stored position server-side, so re-read rather than guess.
          load();
        }
      })
      .catch(() => {})
      .finally(() => {
        if (mounted.current) {
          setSavingSharing(false);
        }
      });
  };

  return (
    <>
      {people.map((person) => {
        const state = resolveState(person);
        if (!state.plottable) {
          return null;
        }

        const age = relativeTime(person.last_updated_at);

        return (
          <Marker
            key={personKey(person)}
            position={[person.latitude, person.longitude]}
            icon={buildIcon(state.tone)}
          >
            <Popup>
              <div style={{ minWidth: 170 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{person.name}</div>
                {personTypeKey(person) && (
                  <div style={{ fontSize: 12, color: '#777' }}>
                    {t(personTypeKey(person))}
                  </div>
                )}
                <div style={{ fontSize: 12, color: '#555', display: 'flex', alignItems: 'center' }}>
                  <Dot tone={state.tone} />
                  {stateLabel(state, t)}
                </div>
                {age && (
                  <div style={{ fontSize: 12, color: '#555', marginTop: 4 }}>{age}</div>
                )}
              </div>
            </Popup>
          </Marker>
        );
      })}

      <Control position="topright">
        <div
          style={{
            background: '#ffffff',
            borderRadius: 4,
            boxShadow: '0 1px 5px rgba(0,0,0,0.4)',
            padding: '8px 10px',
            minWidth: 190,
          }}
        >
          {/* Always visible. The count is what makes a collapsed panel useful:
              it answers "is anyone actually on the map" without expanding. */}
          <div
            role="button"
            tabIndex={0}
            onClick={toggleExpanded}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleExpanded();
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 12,
              userSelect: 'none',
            }}
            title={expanded ? t('propertyMapPage.userLocations.hideList')
                            : t('propertyMapPage.userLocations.showList')}
          >
            {expanded
              ? <DownOutlined style={{ fontSize: 10, marginRight: 6 }} />
              : <RightOutlined style={{ fontSize: 10, marginRight: 6 }} />}
            <span style={{ flex: 1 }}>
              {t('propertyMapPage.userLocations.people')}
            </span>
            <span style={{ fontWeight: 400, color: '#888', marginLeft: 8 }}>
              {locatedCount > 0
                ? t('propertyMapPage.userLocations.located', { count: locatedCount })
                : t('propertyMapPage.userLocations.noneLocated')}
            </span>
          </div>

          {expanded && (
          <div style={{ maxHeight: 240, overflowY: 'auto', marginTop: 6 }}>
          {people.length === 0 && (
            <div style={{ fontSize: 12, color: '#888' }}>
              {t('propertyMapPage.userLocations.noOneToShow')}
            </div>
          )}

          {[...people].sort(compareForRoster).map((person) => {
            const state = resolveState(person);
            const age = relativeTime(person.last_updated_at);

            return (
              <div
                key={personKey(person)}
                style={{ display: 'flex', alignItems: 'center', fontSize: 12, padding: '2px 0' }}
              >
                <Dot tone={state.tone} />
                <span style={{ flex: 1, marginRight: 8 }}>
                  <span style={{ fontWeight: 500 }}>{person.name}</span>
                  <span style={{ color: '#777' }}>
                    {' \u2014 '}
                    {stateLabel(state, t)}
                  </span>
                </span>
                {age && <span style={{ color: '#999', flex: '0 0 auto' }}>{age}</span>}
              </div>
            );
          })}

          </div>
          )}

          {expanded && sharing !== null && (
            <div
              style={{
                borderTop: '1px solid #eee',
                marginTop: 8,
                paddingTop: 8,
                display: 'flex',
                alignItems: 'center',
                fontSize: 12,
              }}
            >
              <span style={{ flex: 1, marginRight: 8 }}>
                {t('propertyMapPage.userLocations.shareMine')}
              </span>
              <Switch
                size="small"
                checked={sharing}
                loading={savingSharing}
                onChange={onToggleSharing}
              />
            </div>
          )}

          {/* Your own pin can come from your phone, in which case this switch
              does not control it -- without saying so, turning it off and
              seeing yourself stay put reads as the switch being broken. */}
          {expanded && ownRowIsFromApp && (
            <div style={{ fontSize: 11, color: '#777', marginTop: 6 }}>
              {t('propertyMapPage.userLocations.pinFromApp')}
            </div>
          )}

          {/* Without this the switch reads as on while nothing is being
              stored, which looks exactly like the feature being broken.

              A refusal carries the server's own reason, because the client
              cannot tell them apart: too imprecise, sharing switched off in
              another tab, and not being a member of this property all arrive
              as success:false. Those messages are already user-facing, and
              surfacing res.message is what the rest of this module does. The
              translated line is only a fallback. */}
          {expanded && sharing === true && REPORT_PROBLEMS[reportStatus] && (
            <div style={{ fontSize: 11, color: '#c0392b', marginTop: 6 }}>
              {reportStatus === 'refused' && reportDetail
                ? reportDetail
                : t(REPORT_PROBLEMS[reportStatus])}
            </div>
          )}
        </div>
      </Control>
    </>
  );
};

export default UserLocationMarkers;
